import { META_ERROR_DOCS } from './ErrorDirectory';
import {
  tLeadsalesRefusalEntry,
  tLeadsalesRefusalFacts,
  tLeadsalesRefusalReason,
  tMetaErrorNotice,
  tMetaErrorNoticeBillingCohort,
  tMetaErrorNoticeEntry,
  tMetaErrorNoticeExternalTarget,
  tMetaErrorNoticeLearnMore,
  tResolveMetaErrorNoticeOptions,
} from './metaErrorNotice.types';

/**
 * INT-1502: the copy behind the notice shown inside a message in the lead's
 * chat, one entry per Meta error code.
 *
 * This file holds only the shape of each notice (variant, title family, how
 * many steps, which action, which notes and links). Every visible string lives
 * in `public/locales/{es,en,br}/chat/metaErrors.json`, so copy can change
 * without touching a component.
 *
 * Source of truth since INT-1500 (2026-09-26): the verified catalog and the
 * "Proposed Spanish copy" tables of
 * docs/plans/2026-09-26-001-fix-wapi-meta-error-notices-and-scheduled-messages-plan.md.
 * Meta's documentation wins on what a code means; the plan wins on copy.
 *
 * - The title names who decided (Meta, the business's account, Leadsales).
 * - Groups share one entry and one copy node, and each keeps its own code:
 *   1·2·131016·133004, 100·131008·131009·135000, 130429·80007·4,
 *   131057·2494100 and 0·3·10·190·200-299·131005.
 * - 131030 and 10015 are gone: neither is in Meta's current documentation and
 *   neither happened in 14 days. They fall back like any unmapped code.
 * - 133000/133005/133006/133015 stay (D11): with the code in sight, support
 *   can tell what happened. Their way out is support, not Business Manager.
 *
 * Scope is the chat only. Banners, toasts and the templates section keep
 * resolving through ErrorDirectory; this catalog does not replace it.
 */

export const META_ERROR_NOTICE_NAMESPACE = 'chat/metaErrors';


/**
 * 131051 and 131060 are webhook errors on Meta's unsupported-messages
 * reference, not on the error-codes page (D10).
 */
export const META_UNSUPPORTED_MESSAGES_DOCS =
  'https://developers.facebook.com/documentation/business-messaging/whatsapp/webhooks/reference/messages/unsupported';

/** Destinations for the external actions. */
export const META_ERROR_NOTICE_EXTERNAL_URLS: Record<
  tMetaErrorNoticeExternalTarget,
  string
> = {
  displayNameInstructions:
    'https://es-la.facebook.com/business/help/378834799515077',
  templatePacing:
    'https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/template-pacing',
};

/** Meta's explainers behind the optional "Cómo funciona" link. */
export const META_ERROR_NOTICE_LEARN_MORE_URLS: Record<
  tMetaErrorNoticeLearnMore,
  string
> = {
  perUserLimits:
    'https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/marketing-templates/per-user-limits',
  experiments:
    'https://developers.facebook.com/documentation/business-messaging/whatsapp/support/experiments/',
  templateCategorization:
    'https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/template-categorization',
  templateQuality: 'https://es-la.facebook.com/business/help/766346674749731',
  whatsappStatus: 'https://metastatus.com/whatsapp-business-api',
};

/**
 * INT-1504: the Meta codes messaging's retry (BUG-908) accepts, its default
 * `WAPI_RETRY_META_ERROR_CODES` (WapiRetryEligibilityEvaluator.ts). A notice
 * whose action is `retry` only shows the button for these: messaging refuses
 * every other code, so 131056, 130429 and 131057 keep their steps and lose the
 * button until that list changes. Messaging still has the last word (text or
 * one file, never accepted by Meta, attempt ceiling, cooldown); a refusal
 * there becomes a toast.
 */
export const META_ERROR_NOTICE_RETRYABLE_CODES: ReadonlySet<string> = new Set([
  '131000',
  '1',
  '2',
  '4',
  '131016',
  '80007',
  '2494100',
  '133004',
]);

const PERMISSION_RANGE_CODE = '200-299';

// `anchor` takes the id without its `#`: a literal like '#130472' reads as a hex
// colour to the DS lint rule.
const anchor = (id: string) => `${META_ERROR_DOCS}#${id}`;
// Meta's page has no per-code anchors; a text fragment scrolls to the code.
const docsFor = (code: string) => anchor(`:~:text=${code}`);
// A one or two digit code would match the first bare digit on the page, so
// those link to the section of the table that lists them instead.
const OTHER_ERRORS = anchor('other-errors');

type tEntryFields = Omit<tMetaErrorNoticeEntry, 'code' | 'direction' | 'copyKey' | 'variant'> &
  Partial<Pick<tMetaErrorNoticeEntry, 'copyKey' | 'variant'>>;

/** A failure of a message the business sent. */
function sent(code: string, fields: tEntryFields): tMetaErrorNoticeEntry {
  return {
    code,
    direction: 'outgoing',
    variant: 'error',
    copyKey: code,
    docsUrl: docsFor(code),
    ...fields,
  };
}

/** A message the lead sent that did not reach us whole. */
function received(code: string, fields: tEntryFields): tMetaErrorNoticeEntry {
  return { ...sent(code, fields), direction: 'incoming' };
}

const entries: tMetaErrorNoticeEntry[] = [
  // ─── Meta limitó el marketing a este lead ───
  // Límite de marketing por persona. D5: the utility-template action only
  // when the workspace has one (useMetaErrorNoticeAction); note U always.
  sent('131049', {
    title: 'templateNotDelivered',
    stepCount: 3,
    action: { kind: 'chooseUtilityTemplate' },
    note: 'industryRule',
    visibleNote: 'utilityTemplateUse',
    learnMore: 'perUserLimits',
    // RET-2256: body closes on "no se descontó de tus créditos" (UC-01).
    hasBillingUnitBody: true,
  }),
  // Experimento de marketing de Meta
  sent('130472', {
    title: 'templateNotDelivered',
    stepCount: 2,
    note: 'industryRule',
    learnMore: 'experiments',
    // RET-2256: body closes on "no se descontó de tus créditos" (UC-01).
    hasBillingUnitBody: true,
  }),
  // El lead no quiere promociones
  sent('131050', { title: 'templateNotDelivered', stepCount: 1, note: 'industryRule' }),

  // ─── La conversación o el número del lead ───
  // Ventana de 24 h cerrada
  sent('131047', {
    title: 'messageNotDelivered',
    stepCount: 2,
    action: { kind: 'sendTemplate' },
    note: 'industryRule',
  }),
  // El número no puede recibir mensajes
  sent('131026', { title: 'messageNotDelivered', stepCount: 2 }),
  // Tú bloqueaste al lead
  sent('130403', { title: 'leadBlocked', stepCount: 2, action: { kind: 'unblockLead' } }),
  // Mismo número que tu negocio
  sent('131021', { title: 'messageNotDelivered', stepCount: 1 }),
  // País no permitido para la cuenta
  sent('130497', { title: 'messageNotDelivered', stepCount: 1, action: { kind: 'contactSupport' } }),
  // Muchos mensajes seguidos al mismo lead. "Reintentar" once INT-1518 lands.
  sent('131056', {
    title: 'messageNotDelivered',
    stepCount: 2,
    action: { kind: 'retry' },
    note: 'industryRule',
  }),

  // ─── La plantilla ───
  // Plantilla pausada por calidad
  sent('132015', {
    title: 'templatePaused',
    stepCount: 2,
    action: { kind: 'chooseOtherTemplate' },
    learnMore: 'templateQuality',
  }),
  // Plantilla desactivada
  sent('132016', { title: 'templateDisabled', stepCount: 2, action: { kind: 'chooseOtherTemplate' } }),
  // Plantilla inexistente o no aprobada (BUG-844 family)
  sent('132001', { title: 'templateRejected', stepCount: 1, action: { kind: 'chooseOtherTemplate' } }),
  // Variables que no coinciden. "Editar mensaje" needs INT-1517.
  sent('132000', { title: 'templateRejected', stepCount: 2, action: { kind: 'editMessage' } }),
  // Formato de variables
  sent('132012', { title: 'templateRejected', stepCount: 2, action: { kind: 'editMessage' } }),
  // Variables que Meta rechazó
  sent('132018', { title: 'templateRejected', stepCount: 2, action: { kind: 'editMessage' } }),
  // Mensaje demasiado largo
  sent('132005', { title: 'templateRejected', stepCount: 1, action: { kind: 'editMessage' } }),
  // Contenido contra políticas
  sent('132007', { title: 'templateRejected', stepCount: 1, action: { kind: 'chooseOtherTemplate' } }),
  // Marketing desactivado en la API
  sent('131063', { title: 'templateRejected', stepCount: 1, action: { kind: 'contactSupport' } }),

  // ─── Tu cuenta o tu número de WhatsApp Business ───
  // Cuenta restringida por Meta
  sent('131031', { title: 'accountRestricted', stepCount: 2, action: { kind: 'viewAccountStatus' } }),
  // Cuenta restringida por política
  sent('368', { title: 'accountRestricted', stepCount: 2, action: { kind: 'viewAccountStatus' } }),
  // Número limitado por spam
  sent('131048', { title: 'numberLimited', stepCount: 2, action: { kind: 'viewNumberQuality' } }),
  // Límite por usar la categoría equivocada
  sent('131064', { title: 'numberLimited', stepCount: 1, learnMore: 'templateCategorization' }),
  // Límite de velocidad: one entry, 130429 reads differently from 80007 and 4.
  ...['130429', '80007', '4'].map((code) =>
    sent(code, {
      title: 'throttled',
      copyKey: 'throttled',
      body: code === '130429' ? 'bodyNumber' : undefined,
      stepCount: 1,
      action: { kind: 'retry' },
      docsUrl: code === '4' ? anchor('throttling-errors') : docsFor(code),
    }),
  ),
  // Método de pago: Leadsales' credit line or a pending business verification.
  sent('131042', { title: 'paymentMethod', stepCount: 1, action: { kind: 'contactSupport' } }),
  // Número no registrado en la API (coex activation family, INT-1516)
  sent('133010', { title: 'numberNotActive', stepCount: 2, action: { kind: 'reviewConnection' } }),
  // Error en el registro del número
  sent('131045', { title: 'numberNotActive', stepCount: 2, action: { kind: 'contactSupport' } }),
  // Número eliminado de la cuenta
  sent('33', {
    title: 'numberNotActive',
    stepCount: 2,
    action: { kind: 'contactSupport' },
    docsUrl: OTHER_ERRORS,
  }),
  // Número virtual sin nombre visible
  sent('131037', {
    title: 'testNumber',
    stepCount: 2,
    action: { kind: 'external', target: 'displayNameInstructions' },
  }),
  // Registro del número en la API (D11)
  sent('133000', { title: 'numberNotActive', stepCount: 1, action: { kind: 'contactSupport' } }),
  sent('133005', { title: 'numberNotActive', stepCount: 1, action: { kind: 'contactSupport' } }),
  sent('133006', { title: 'numberNotActive', stepCount: 1, action: { kind: 'contactSupport' } }),
  sent('133015', { title: 'numberNotActive', stepCount: 2, action: { kind: 'contactSupport' } }),
  // Cuenta en mantenimiento
  ...['131057', '2494100'].map((code) =>
    sent(code, {
      title: 'maintenance',
      copyKey: 'maintenance',
      stepCount: 1,
      action: { kind: 'retry' },
    }),
  ),
  // Permisos de la conexión (200-299 is any code in that range)
  ...['0', '3', '10', '190', PERMISSION_RANGE_CODE, '131005'].map((code) =>
    sent(code, {
      title: 'permissionLost',
      copyKey: 'permissionLost',
      stepCount: 1,
      action: { kind: 'contactSupport' },
      docsUrl: anchor('authorization-errors'),
    }),
  ),

  // ─── La plataforma de Meta o los datos del mensaje ───
  // Falla temporal de Meta
  sent('131000', { title: 'metaTemporaryFailure', stepCount: 2, action: { kind: 'retry' } }),
  // Servicio de WhatsApp sin responder
  ...['1', '2', '131016', '133004'].map((code) =>
    sent(code, {
      title: 'metaTemporaryFailure',
      copyKey: 'metaUnavailable',
      stepCount: 1,
      action: { kind: 'retry' },
      learnMore: 'whatsappStatus',
      docsUrl: code.length < 3 ? OTHER_ERRORS : docsFor(code),
    }),
  ),
  // Un dato del envío no es válido: usually how we built the payload.
  ...['100', '131008', '131009', '135000'].map((code) =>
    sent(code, {
      title: 'invalidData',
      copyKey: 'invalidData',
      stepCount: 1,
      action: { kind: 'contactSupport' },
    }),
  ),
  // Archivo que Meta no pudo procesar. D14: support, never a retry.
  // BUG-912: `fileGone` when Meta could not fetch the file because it was
  // deleted from the workspace's storage (the caller reads that from Meta's
  // details). Support cannot bring the file back; attaching it again does, so
  // this wording has its own steps and no action.
  sent('131053', {
    title: 'fileNotDelivered',
    stepCount: 1,
    action: { kind: 'contactSupport' },
    // INT-1683: `webp` when Meta's details say it does not take WebP images.
    // Support cannot change the format; saving it as JPG or PNG does.
    copyVariants: {
      fileGone: { stepCount: 2, action: null },
      webp: { stepCount: 2, action: null },
    },
  }),

  // ─── Mensajes del lead ───
  // Mensaje que Meta no comparte (D10). D15: "Hablar con soporte" starts the
  // move to Cloud API only.
  received('131060', {
    variant: 'info',
    title: 'messageUnavailable',
    stepCount: 2,
    action: { kind: 'talkToSupport' },
    docsUrl: META_UNSUPPORTED_MESSAGES_DOCS,
    echoes: true,
  }),
  // Tipo de mensaje que la API no admite: an edit, or the business's own
  // message echoed from the WhatsApp Business app.
  received('131051', {
    variant: 'info',
    title: 'messageUnavailable',
    stepCount: 1,
    docsUrl: META_UNSUPPORTED_MESSAGES_DOCS,
    copyVariants: { edit: {}, echo: { stepCount: 1 } },
    echoes: true,
  }),
  // Archivo del lead que Meta no descargó
  received('131052', { title: 'fileNotReceived', stepCount: 1 }),
];

export const metaErrorNoticeCatalog = new Map<string, tMetaErrorNoticeEntry>(
  entries.map((entry) => [entry.code, entry]),
);

/**
 * INT-1500 U3 (R6): what a code the catalog does not explain gets. It says so
 * plainly, shows the code and sends the seller to support; it never borrows
 * 131000's copy, which would call a known error "unknown". Unmapped codes are
 * reported to telemetry (useUnmappedNoticeTelemetry).
 */
export const META_ERROR_NOTICE_UNMAPPED: tMetaErrorNoticeEntry = {
  code: 'unmapped',
  direction: 'outgoing',
  variant: 'error',
  title: 'messageNotDelivered',
  copyKey: 'unmapped',
  stepCount: 1,
  action: { kind: 'contactSupport' },
};

function findEntry(code: string): tMetaErrorNoticeEntry | undefined {
  const direct = metaErrorNoticeCatalog.get(code);
  if (direct) return direct;

  const numeric = Number(code);
  if (Number.isInteger(numeric) && numeric >= 200 && numeric <= 299) {
    return metaErrorNoticeCatalog.get(PERMISSION_RANGE_CODE);
  }

  return undefined;
}

/**
 * The action the notice actually offers for the code that arrived: the
 * catalog's, except a `retry` messaging would refuse for this code.
 */
function actionFor(
  entry: tMetaErrorNoticeEntry,
  incoming: string | undefined,
): tMetaErrorNoticeEntry['action'] {
  if (entry.action?.kind !== 'retry') return entry.action;
  return incoming && META_ERROR_NOTICE_RETRYABLE_CODES.has(incoming)
    ? entry.action
    : undefined;
}

function actionKeyFor(entry: tMetaErrorNoticeEntry): string | undefined {
  if (!entry.action) return undefined;
  if (entry.action.kind === 'external') {
    return `actions.external.${entry.action.target}`;
  }
  return `actions.${entry.action.kind}`;
}

/**
 * INT-1500 U5 (INT-1507): Meta's `held_for_quality_assessment` message status
 * (synthetic Meta 900001, wba-10060, chats-service 3048). Not an error code:
 * Meta holds the message for a quality review before delivering it. The
 * notice waits with the seller, shows no code, and gives way on delivery or to
 * the failure notice when Meta refuses (132015 under template pacing, 135000
 * under portfolio pacing). The status does not say which pacing holds it; the
 * button opens template pacing, the common case. The prototype's "500 mil
 * mensajes diarios" misreads portfolio pacing (500K template messages over 365
 * days), so the copy makes no volume claim.
 */
export const META_ERROR_NOTICE_HELD_FOR_QUALITY: tMetaErrorNoticeEntry = {
  code: 'heldForQuality',
  direction: 'outgoing',
  variant: 'pending',
  title: 'inReview',
  copyKey: 'heldForQuality',
  stepCount: 0,
  action: { kind: 'external', target: 'templatePacing' },
};

/**
 * BUG-827, BUG-1141: WhatsApp Web's new-chat limit (chat code 2011). WhatsApp
 * refused a send to a lead who has not written recently. Two refusals share
 * the code: WhatsApp's ack 475 on the send (the line's quota of new chats,
 * measured 2026-10-29) and a 429 on the number lookup, which was not the quota
 * in every case that could be checked (BUG-827). The front cannot tell which
 * one hit, so the copy states the effect and promises no date or quota. Not a
 * Meta code and not a WhatsApp API failure, but the seller needs what a Meta
 * refusal gives them: who decided, that the session is fine, and that it lifts
 * on its own. So it shows the same notice, with no code (WhatsApp gives none).
 * The steps say what still works: the line's own phone app delivered to leads
 * Leadsales could not reach (2026-10-29), and replies to leads who wrote
 * recently still go out. "Hablar con soporte" is for the seller who needs
 * more new conversations: support explains the WhatsApp Business API.
 */
export const META_ERROR_NOTICE_WHATSAPP_WEB_NEW_CHAT_LIMIT: tMetaErrorNoticeEntry = {
  code: 'whatsappWebNewChatLimit',
  direction: 'outgoing',
  variant: 'error',
  title: 'whatsappWebNotSent',
  copyKey: 'whatsappWebNewChatLimit',
  stepCount: 4,
  action: { kind: 'talkToSupport' },
};

/** The notice for a WhatsApp Web send refused by WhatsApp's new-chat limit (2011). */
export function resolveWhatsappWebNewChatLimitNotice(): tMetaErrorNotice {
  const entry = META_ERROR_NOTICE_WHATSAPP_WEB_NEW_CHAT_LIMIT;
  const copyBase = `codes.${entry.copyKey}`;
  return {
    ...entry,
    displayCode: undefined,
    isFallback: false,
    // WhatsApp never sent it: "No enviado", not "No entregado" (D7).
    notSent: true,
    titleKey: `titles.${entry.title}`,
    bodyKey: `${copyBase}.body`,
    stepKeys: Array.from({ length: entry.stepCount }, (_, index) => `${copyBase}.steps.${index + 1}`),
    actionKey: actionKeyFor(entry),
    supportReasonKey: `${copyBase}.supportReason`,
  };
}

/**
 * BUG-1030: WhatsApp Web sent to a number WhatsApp has no account for (chat
 * code 1002). whatsmeow's identity lookup answered "no LID found", so nothing
 * left. Usually the number is mistyped: in Mexico a mobile written as 52 + 10
 * digits, without the 1, is a different WhatsApp ID from 52 1 + 10 digits, and
 * only the second one exists. Same notice as the new-chat limit, with no code
 * (WhatsApp gives none), no action and no link: the fix is the seller's own
 * data, so the steps say how to check the number.
 */
export const META_ERROR_NOTICE_WHATSAPP_WEB_RECIPIENT_NOT_FOUND: tMetaErrorNoticeEntry = {
  code: 'whatsappWebRecipientNotFound',
  direction: 'outgoing',
  variant: 'error',
  title: 'whatsappWebNotSent',
  copyKey: 'whatsappWebRecipientNotFound',
  stepCount: 3,
};

/** The notice for a WhatsApp Web send to a number WhatsApp has no account for (1002). */
export function resolveWhatsappWebRecipientNotFoundNotice(): tMetaErrorNotice {
  const entry = META_ERROR_NOTICE_WHATSAPP_WEB_RECIPIENT_NOT_FOUND;
  const copyBase = `codes.${entry.copyKey}`;
  return {
    ...entry,
    displayCode: undefined,
    isFallback: false,
    // WhatsApp never sent it: "No enviado", not "No entregado" (D7).
    notSent: true,
    titleKey: `titles.${entry.title}`,
    bodyKey: `${copyBase}.body`,
    stepKeys: Array.from({ length: entry.stepCount }, (_, index) => `${copyBase}.steps.${index + 1}`),
  };
}

/** The pending notice for a message Meta holds for a quality review. */
export function resolveHeldForQualityNotice(): tMetaErrorNotice {
  const entry = META_ERROR_NOTICE_HELD_FOR_QUALITY;
  return {
    ...entry,
    displayCode: undefined,
    isFallback: false,
    titleKey: `titles.${entry.title}`,
    bodyKey: `codes.${entry.copyKey}.body`,
    stepKeys: [],
    actionKey: actionKeyFor(entry),
  };
}

/** The support actions whose message carries a per-code reason (D17). */
const SUPPORT_ACTIONS: ReadonlySet<string> = new Set([
  'contactSupport',
  'talkToSupport',
]);

/** Whether an action sends the seller's message to support (D15, D17). */
export function isSupportAction(kind: string): boolean {
  return SUPPORT_ACTIONS.has(kind);
}

/**
 * Resolves the notice for a Meta error code, in the wording the caller asks
 * for when the entry has one (131051: `edit`, `echo`; 131053: `fileGone`).
 *
 * An unmapped code gets the unmapped copy with the code that arrived. Its link
 * points at Meta's error table, and only when the code is numeric: a
 * non-numeric code is ours, not Meta's, and their page cannot explain it.
 */
export function resolveMetaErrorNotice(
  code: number | string | undefined | null,
  options: tResolveMetaErrorNoticeOptions = {},
): tMetaErrorNotice {
  const incoming =
    code === undefined || code === null || code === ''
      ? undefined
      : String(code);
  const entry = incoming ? findEntry(incoming) : undefined;
  const isFallback = !entry;
  const resolved = entry ?? META_ERROR_NOTICE_UNMAPPED;

  const docsUrl = isFallback
    ? incoming && /^\d+$/.test(incoming)
      ? META_ERROR_DOCS
      : undefined
    : resolved.docsUrl;

  const copyBase = `codes.${resolved.copyKey}`;
  const copyVariant = options.copyVariant;
  const variantCopy = copyVariant ? resolved.copyVariants?.[copyVariant] : undefined;
  const bodyKey = variantCopy
    ? `${copyBase}.${copyVariant}.body`
    : `${copyBase}.${resolved.body ?? 'body'}`;
  const ownStepCount = variantCopy?.stepCount;
  const stepsBase =
    ownStepCount === undefined
      ? `${copyBase}.steps`
      : `${copyBase}.${copyVariant}.steps`;
  const stepCount = ownStepCount ?? resolved.stepCount;
  // A wording keeps the entry's action unless it names its own, or none.
  const worded =
    variantCopy && 'action' in variantCopy
      ? { ...resolved, action: variantCopy.action ?? undefined }
      : resolved;

  const offered = { ...worded, stepCount, action: actionFor(worded, incoming) };

  return {
    ...offered,
    docsUrl,
    displayCode: incoming,
    isFallback,
    titleKey: `titles.${resolved.title}`,
    bodyKey,
    bodyValues: isFallback && incoming ? { code: incoming } : undefined,
    stepKeys: Array.from(
      { length: stepCount },
      (_, index) => `${stepsBase}.${index + 1}`,
    ),
    actionKey: actionKeyFor(offered),
    noteKey: resolved.note ? `notes.${resolved.note}` : undefined,
    visibleNoteKey: resolved.visibleNote
      ? `notes.${resolved.visibleNote}`
      : undefined,
    learnMoreLink: resolved.learnMore
      ? {
          labelKey: `learnMore.${resolved.learnMore}`,
          href: META_ERROR_NOTICE_LEARN_MORE_URLS[resolved.learnMore],
        }
      : undefined,
    supportReasonKey:
      worded.action && isSupportAction(worded.action.kind)
        ? `${copyBase}.supportReason`
        : undefined,
  };
}

/**
 * The copy key each non-credits cohort reads (RET-2339, D10). A `Record` so a
 * new cohort fails to compile until it is mapped here, instead of silently
 * falling into the no-unit wording.
 */
const BODY_KEY_SUFFIX_BY_COHORT: Record<
  Exclude<tMetaErrorNoticeBillingCohort, 'credits'>,
  'bodyWallet' | 'bodyNoUnit'
> = {
  wallet: 'bodyWallet',
  unresolved: 'bodyNoUnit',
  noWapiAccess: 'bodyNoUnit',
};

/**
 * RET-2256: the cohort table, in one place. A resolved notice whose entry
 * declares `hasBillingUnitBody` reads a cohort-specific body: `bodyWallet`
 * for the wallet cohort, `bodyNoUnit` while the cohort has not resolved for
 * this workspace or when it is known to have no WhatsApp API (RET-2339), and
 * `notice.bodyKey` (today's `body`) for the credits cohort — the only one where the claim in `body` still holds. Every other
 * entry keeps `notice.bodyKey` in every cohort: this function is the only
 * place a code's billing wording is singled out, never a renderer.
 *
 * Pure and React-free like the rest of this module: callers pass in the
 * cohort they read from `useWalletFlag` (via the shared
 * `useMetaErrorNoticeBillingCohort` hook), this function does not read it
 * itself.
 */
export function bodyKeyForBillingCohort(
  notice: tMetaErrorNotice,
  cohort: tMetaErrorNoticeBillingCohort,
): string {
  if (!notice.hasBillingUnitBody || cohort === 'credits') return notice.bodyKey;
  const copyBase = notice.bodyKey.slice(0, notice.bodyKey.lastIndexOf('.'));
  return `${copyBase}.${BODY_KEY_SUFFIX_BY_COHORT[cohort]}`;
}

/** A send Leadsales refused before Meta: the "ours" variant, no Meta docs of its own. */
function refused(
  error: string,
  fields: Omit<tLeadsalesRefusalEntry, 'code' | 'direction' | 'variant'>,
): tLeadsalesRefusalEntry {
  return { code: error, direction: 'outgoing', variant: 'ours', ...fields };
}

/**
 * INT-1500 U7 (contract C2, D2, D7): the plan's "Leadsales refused the send"
 * rows, keyed by the chat error name both paths deliver (bff-web's REST name,
 * or the socket's number through parseErrors).
 *
 * The title says Leadsales decided (R2) and the notice shows no Meta code of
 * its own (D2). A per-lead hold shows the Meta code that armed it instead,
 * linked to Meta: 131049 for the back-off (INT-1314), 131050 for the opt-out.
 *
 * The wallet refusals (chat codes 3068-3070) are here since RET-2257. They are
 * the only rows whose fix is money and not content, so each one gets its own
 * exit: top up, retry, or support. Not here yet: the throughput limiter.
 */
const leadsalesRefusals: tLeadsalesRefusalEntry[] = [
  // Per-lead marketing back-off: Meta rejected marketing to this lead with
  // 131049, so marketing waits 24 h (INT-1314, MARKETING_BACKOFF_HOURS).
  refused('WAPI_MARKETING_BACKOFF_ERROR', {
    title: 'leadsalesNotSent',
    copyKey: 'marketingBackoff',
    body: 'bodyUndated',
    stepCount: 0,
    sourceMetaCode: '131049',
    datesTheHold: true,
  }),
  // The lead opted out of marketing (Meta 131050). It never lifts on its own.
  refused('WAPI_MARKETING_OPT_OUT_ERROR', {
    title: 'leadsalesNotSent',
    copyKey: 'marketingOptOut',
    stepCount: 0,
    sourceMetaCode: '131050',
  }),
  // Marketing paused while Meta restricts the number (INT-1205, wba-10092).
  refused('WAPI_MARKETING_PAUSED_RESTRICTED_ERROR', {
    title: 'marketingPaused',
    copyKey: 'marketingPausedRestricted',
    stepCount: 0,
    action: { kind: 'viewAccountStatus' },
  }),
  // No template credits left (wba-10024, wba-10025).
  refused('WAPI_TEMPLATE_CREDITS_ERROR', {
    title: 'noCredits',
    copyKey: 'templateCredits',
    stepCount: 0,
    action: { kind: 'viewCredits' },
  }),
  // A predefined variable the lead has no value for (wba-10053). WBS does not
  // say which, so the body names none.
  refused('WAPI_TEMPLATE_EMPTY_VARIABLES_ERROR', {
    title: 'missingLeadData',
    copyKey: 'emptyVariables',
    stepCount: 0,
    action: { kind: 'editLead' },
  }),
  // BUG-844: a scheduled template deleted or unapproved before it went out
  // (wba-10122). The plan gives it 132001's copy; Meta never answered, so it
  // shows no code.
  refused('WAPI_SCHEDULED_TEMPLATE_DELETED_ERROR', {
    title: 'templateRejected',
    copyKey: '132001',
    stepCount: 1,
    action: { kind: 'chooseOtherTemplate' },
  }),
  // RET-2257: el saldo no alcanzó. La salida es recargar, así que la acción
  // lleva a la misma pantalla que el contador — el copy de esa pantalla ya
  // distingue créditos de saldo por cohorte (RET-2256).
  //
  // Sin monto a propósito: la app todavía no lee `availableMicros` /
  // `requiredMicros` del evento, y un importe inventado aquí sería peor que no
  // darlo. Cuando se lean, esta entrada gana su `bodyValues`.
  refused('WAPI_WALLET_INSUFFICIENT_BALANCE_ERROR', {
    title: 'walletNoBalance',
    copyKey: 'walletNoBalance',
    stepCount: 1,
    action: { kind: 'viewCredits' },
  }),
  // El wallet no respondió. El mensaje NO salió por este camino, así que
  // reintentar es la salida correcta y barata.
  refused('WAPI_WALLET_UNAVAILABLE_ERROR', {
    title: 'walletUnavailable',
    copyKey: 'walletUnavailable',
    stepCount: 1,
    action: { kind: 'retry' },
  }),
  // No se pudo tarifar al destinatario. Reintentar no lo arregla —la tarifa no
  // existe, no es un fallo transitorio— así que la acción es soporte.
  refused('WAPI_WALLET_UNPRICEABLE_ERROR', {
    title: 'walletUnpriceable',
    copyKey: 'walletUnpriceable',
    stepCount: 1,
    action: { kind: 'contactSupport' },
  }),
  // BUG-1057: the workspace has no WhatsApp Business API account to send from
  // (wba-10125), typically after a downgrade while the lead's chat still routes
  // to WHATSAPP_API. A retired account and one that never existed look the same
  // to the sender, so the copy never says "ya no". No step and no action: the
  // obvious way out, replying through WhatsApp Web, does not work yet (BUG-810
  // reopens the same WHATSAPP_API chat) and the workspace may have no WhatsApp
  // Web at all. Add a step gated on a connected WhatsApp Web once BUG-810 ships.
  refused('WAPI_ACCOUNT_NOT_CONNECTED_ERROR', {
    title: 'wabaNotConnected',
    copyKey: 'wabaNotConnected',
    stepCount: 0,
  }),
  // BUG-1104: whatsapp-business-service checked the sticker before the send
  // and it misses what Meta requires of a sticker through WhatsApp API
  // (wba-10126). The body names the limits. No step: sending it as an image or
  // a file does not work either, Meta refuses .webp images too (131053).
  refused('WAPI_STICKER_NOT_SENDABLE_ERROR', {
    title: 'stickerNotSendable',
    copyKey: 'stickerNotSendable',
    stepCount: 0,
  }),
  // BUG-1104: a message type whatsapp-business-service cannot send through
  // WhatsApp API (wba-10127), today a forwarded contact card. It is ours to
  // fix, not the seller's, so no step and no action.
  refused('WAPI_MESSAGE_TYPE_NOT_SENDABLE_ERROR', {
    title: 'messageTypeNotSendable',
    copyKey: 'messageTypeNotSendable',
    stepCount: 0,
  }),
  // INT-1683: whatsapp-business-service read the image's header before the
  // send and WhatsApp would refuse it (wba-10128). The reason token in the
  // error details picks the wording; an unknown one reads the entry's own.
  // No retry (the same image is refused again) and no support: the way out
  // is attaching it again from the device, where the composer converts or
  // compresses it. A later text does not deliver the photo, so the notice
  // stays when one goes out.
  refused('WAPI_IMAGE_NOT_SENDABLE_ERROR', {
    title: 'imageNotSendable',
    copyKey: 'imageNotSendable',
    stepCount: 1,
    keepsNoticeAfterLaterSend: true,
    reasons: {
      webp: { title: 'imageNotSendableWebp', copy: 'webp', stepCount: 2 },
      too_large: { title: 'imageNotSendableTooLarge', copy: 'tooLarge', stepCount: 1 },
      bit_depth: { title: 'imageNotSendableBitDepth', copy: 'bitDepth', stepCount: 1 },
    },
  }),
];

export const leadsalesRefusalCatalog: ReadonlyMap<string, tLeadsalesRefusalEntry> = new Map(
  leadsalesRefusals.map((entry) => [entry.code, entry]),
);

/** A whole-number code as a string; it crosses two services as a number or a digit string. */
function wholeNumberCode(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isInteger(value)) return String(value);
  if (typeof value === 'string' && /^\d+$/.test(value)) return value;
  return undefined;
}

function isoDate(value: unknown): string | undefined {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : undefined;
}

/**
 * INT-1500 U7: the notice for a send Leadsales refused before it reached Meta,
 * or undefined when the error is not one of ours.
 *
 * The back-off names Meta's rejection and when the hold lifts when the error
 * row has both dates (a scheduled card, C4); a 1:1 template's row has neither,
 * and it reads without them.
 */
export function resolveLeadsalesRefusalNotice(
  error: string | null | undefined,
  facts: tLeadsalesRefusalFacts = {},
): tMetaErrorNotice | undefined {
  const entry = error ? leadsalesRefusalCatalog.get(error) : undefined;
  if (!entry) return undefined;

  const copyBase = `codes.${entry.copyKey}`;
  const sourceCode = entry.sourceMetaCode
    ? wholeNumberCode(facts.sourceMetaCode) ?? entry.sourceMetaCode
    : undefined;
  const failedOn = isoDate(facts.sourceFailedAt);
  const until = isoDate(facts.backoffUntil);
  const bodyDates = entry.datesTheHold && failedOn && until ? { failedOn, until } : undefined;
  const reason = reasonOf(entry, facts.details);
  const wordingBase = reason ? `${copyBase}.${reason.copy}` : copyBase;
  const stepCount = reason?.stepCount ?? entry.stepCount;

  return {
    ...entry,
    displayCode: sourceCode,
    docsUrl: sourceCode ? docsFor(sourceCode) : undefined,
    isFallback: false,
    notSent: true,
    titleKey: `titles.${reason?.title ?? entry.title}`,
    bodyKey: reason
      ? `${wordingBase}.body`
      : `${copyBase}.${bodyDates ? 'body' : entry.body ?? 'body'}`,
    bodyValues: sourceCode ? { code: sourceCode } : undefined,
    bodyDates,
    stepCount,
    stepKeys: Array.from(
      { length: stepCount },
      (_, index) => `${wordingBase}.steps.${index + 1}`,
    ),
    actionKey: actionKeyFor(entry),
  };
}

/**
 * INT-1683: the wording a refusal's reason token picks, only when the token
 * is a string the entry declares. Own keys only, so `constructor` or
 * `toString` never read as a reason.
 */
function reasonOf(
  entry: tLeadsalesRefusalEntry,
  details: unknown,
): tLeadsalesRefusalReason | undefined {
  if (!entry.reasons || typeof details !== 'string') return undefined;
  return Object.prototype.hasOwnProperty.call(entry.reasons, details)
    ? entry.reasons[details]
    : undefined;
}

/**
 * The status next to a failed bubble's time: "En revisión" while Meta
 * reviews it, "No enviado" when Leadsales refused it (D7), "No entregado"
 * otherwise.
 */
export function noticeStatusKey(
  notice: Pick<tMetaErrorNotice, 'variant' | 'notSent'>,
): string {
  if (notice.variant === 'pending') return 'status.inReview';
  return notice.notSent ? 'status.notSent' : 'status.notDelivered';
}

/** The notice without "Reintentar": a template or a scheduled card is never retried from it. */
export function withoutRetryAction(notice: tMetaErrorNotice): tMetaErrorNotice {
  if (notice.action?.kind !== 'retry') return notice;
  return { ...notice, action: undefined, actionKey: undefined };
}

/**
 * INT-1500 U9: what a failed scheduled card shows when neither catalog
 * explains it: chat code 3065 with a wire code nobody maps yet, or a card from
 * before INT-1500 with only the campaign's free text. It replaces "El envío
 * programado no pudo completarse" and blames no one: nobody said Meta refused.
 */
export const META_ERROR_NOTICE_SCHEDULED_FAILED: tMetaErrorNoticeEntry = {
  code: 'scheduledFailed',
  direction: 'outgoing',
  variant: 'error',
  title: 'scheduledFailed',
  copyKey: 'scheduledFailed',
  stepCount: 1,
  action: { kind: 'contactSupport' },
};

// What messaging stores as the reason when the campaign gave none. It says
// nothing, so it is not worth a "Detalle técnico".
const EMPTY_SCHEDULED_FAILURE_REASON = 'El envío programado no pudo completarse.';

/**
 * INT-1500 U9: the generic notice for a failed scheduled card (see
 * META_ERROR_NOTICE_SCHEDULED_FAILED). It shows the wire code WBS closed the
 * campaign with, else the card's chat code, and sends the seller to support
 * with it; with no code at all it only explains. A wire code the catalog does
 * not know is reported like an unmapped Meta code (R6). The campaign's own
 * reason goes to "Detalle técnico", for support.
 */
export function resolveScheduledFailedNotice(facts: {
  /** The card's chat error code. */
  code?: number;
  /** The wire code (`wba-*`) the campaign failed with. */
  failureCode?: string;
  /** The campaign's reason, free text. */
  context?: string;
}): tMetaErrorNotice {
  const failureCode = facts.failureCode?.trim() || undefined;
  const displayCode = failureCode ?? (facts.code ? String(facts.code) : undefined);
  const entry: tMetaErrorNoticeEntry = displayCode
    ? META_ERROR_NOTICE_SCHEDULED_FAILED
    : { ...META_ERROR_NOTICE_SCHEDULED_FAILED, stepCount: 0, action: undefined };
  const copyBase = `codes.${entry.copyKey}`;
  const context = facts.context?.trim();

  return {
    ...entry,
    displayCode,
    docsUrl: undefined,
    isFallback: Boolean(failureCode),
    titleKey: `titles.${entry.title}`,
    bodyKey: `${copyBase}.body`,
    stepKeys: Array.from(
      { length: entry.stepCount },
      (_, index) => `${copyBase}.steps.${index + 1}`,
    ),
    actionKey: actionKeyFor(entry),
    supportReasonKey: entry.action ? `${copyBase}.supportReason` : undefined,
    technicalDetail:
      context && context !== EMPTY_SCHEDULED_FAILURE_REASON ? context : undefined,
  };
}

